// ========================= 移动端Header容器组件 =========================
//
// 主要功能：
// 1. Header滚动行为控制（通过useHeaderScrollBehavior Hook）
// 2. 用户认证流程管理（通过useUserAuthenticationFlow Hook）
// 3. 聊天组件显示控制
// 4. 各种初始化服务（Reddit分析、MyFit信息、国家信息等）
// 5. Header容器配置和动画效果
//
// 重构说明：将复杂的滚动逻辑和用户认证逻辑提取为自定义Hook，
// 提升代码的可维护性和可读性，同时保持原有功能不变
// =========================================================================

// 移动端header-bar + 对应选项功能
// 菜单栏 + 菜单栏引用模块
"use client";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import stl from "./styles/appBar.module.scss";
import classnames from "classnames";
import { getLocalItem, includesOneOfArr } from "@/lib/commonService";
import { MYFIT } from "@/config/constConf";
import { findMyFit, loginState, signInModal, order } from "@/store";
import { headerStore } from "@/store/pageModules";
import { zIndexAppBarInit } from "@/store/pageModules/header";
import { useCommonContext } from "@/src/provider";
import reddit from "../../LoadAnalysis/analysis/reddit";
import FmStack from "@/ui-component/basic/FmStack";
import { enforceZIndexUnderCurrentId } from "../../FilterNavBar/const";
import { getCountryInfo } from "@/src/models/common";

import { useHeaderScrollBehavior } from "@/src/hooks/useHeaderScrollBehavior";
import { useUserAuthenticationFlow } from "@/src/hooks/useUserAuthenticationFlow";

// ========================= 常量定义 =========================
const ANIMATION_DURATION = 225; // 收起弹窗的动效时间
const CHAT_WIDGET_SELECTOR = "#chat-widget-container";
const REVE_CHAT_SELECTOR = "#reve-chat-container-div";
const LOGIN_PATHS = ["main_page=login", "/login"];

// ========================= 接口定义 =========================
interface IHeaderContainerProps {
    ctlBubbleMark?: "Y" | "N";
    hiddenWhenNoFixed?: boolean;
}

const defaultProps = {
    ctlBubbleMark: "N",
};
const HeaderContainerClient: React.FC<
    React.PropsWithChildren<IHeaderContainerProps>
> = (prop) => {
    const props = { ...defaultProps, ...prop };
    const [
        isShowUserContent,
        starkSx,
        headerContentHeight,
        anouncementHeaderHeight,
        setHeaderOptions,
    ] = headerStore.useHeaderStore((state) => [
        state.isShowUserContent,
        state.starkSx,
        state.headerContentHeight,
        state.anouncementHeaderHeight,
        state.setHeaderOptions,
    ]); // user-login显示or隐藏
    const { host } = useCommonContext();
    const headerRef = useRef<HTMLDivElement>(null);

    const totalNum = order.useOrderStore((state) => state.totalNum);

    const [setLoginState, setLogoutState] = loginState.useLoginStore(
        (state) => [state.setLoginState, state.setLogoutState],
    );

    const visible = signInModal.useSignInModalStore((state) => state.visible);

    const setFrameInfo = findMyFit.useFindMyFitStore(
        (state) => state.setFrameInfo,
    );
    const ctrBubbleByBackend = useCallback(() => {
        if (props.ctlBubbleMark === "Y") {
            // setBubbleIcon(true);
        } else if (props.ctlBubbleMark === "N") {
            // setBubbleIcon(false);
        }
    }, [props.ctlBubbleMark]);

    // ========================= 聊天组件控制逻辑 =========================

    // 判断是否显示聊天组件
    const shouldHideChat = useCallback((url: string) => {
        const isLoginPage = includesOneOfArr(url, LOGIN_PATHS);
        const isCheckoutPage = location.href.includes("checkout");
        return isLoginPage || isCheckoutPage;
    }, []);

    // 控制聊天DOM显示隐藏
    const toggleChatDisplay = useCallback(
        (element: HTMLElement | null, show: boolean) => {
            if (element) {
                element.style.display = show ? "block" : "none";
            }
        },
        [],
    );

    // 判断是否加气泡和livechat气泡
    const showBubbleAndChat = useCallback(
        (url: string) => {
            const shouldShow = !shouldHideChat(url);

            // 获取聊天DOM元素
            const liveChatDom: HTMLDivElement | null =
                document.querySelector(CHAT_WIDGET_SELECTOR);
            const reveChatDom: HTMLDivElement | null =
                document.querySelector(REVE_CHAT_SELECTOR);

            // 控制聊天组件显示
            toggleChatDisplay(liveChatDom, shouldShow);
            toggleChatDisplay(reveChatDom, shouldShow);

            // 是否由后台控制气泡
            if (props.ctlBubbleMark) {
                ctrBubbleByBackend();
            }
        },
        [
            shouldHideChat,
            toggleChatDisplay,
            ctrBubbleByBackend,
            props.ctlBubbleMark,
        ],
    );

    const getCountryInformation = useCallback(async () => {
        try {
            const res = await getCountryInfo();
            let country = "";
            let region = "";
            if (res?.geo?.country) {
                country = res.geo.country;
                region = res.geo.region;
                // setUserCountry(res.geo.country);
            }
            //Reddit 初始化需要判断加州，应对CCPA法案，详情见https://reddit.my.site.com/helpcenter/s/article/Limited-Data-Use
            reddit.init({ country, region, domain: host });
        } catch (error) {
            reddit.init({ domain: host });
        }
    }, [host]);

    // ========================= 初始化服务相关逻辑 =========================

    const readMyFitInfoFromStorage = useCallback(() => {
        const myfit = getLocalItem(MYFIT);
        if (myfit) {
            setFrameInfo(myfit);
        }
    }, [setFrameInfo]);

    // ========================= Hook调用 =========================

    // 滚动行为Hook
    useHeaderScrollBehavior({
        headerRef,
        headerContentHeight,
        hiddenWhenNoFixed: props.hiddenWhenNoFixed,
        anouncementHeaderHeight,
        setHeaderOptions,
    });

    // 用户认证流程Hook
    useUserAuthenticationFlow({
        setLoginState,
        setLogoutState,
        totalNum,
    });

    // ========================= 副作用管理 =========================

    // 1. 组件初始化 - 聊天控制、MyFit信息、国家信息
    useEffect(() => {
        showBubbleAndChat(location.href);
        readMyFitInfoFromStorage();
        getCountryInformation();
    }, [showBubbleAndChat, readMyFitInfoFromStorage, getCountryInformation]);

    // 2. Header容器配置和动画控制
    useEffect(() => {
        // 初始化设置headerContainerHeight
        const headerRefHeight = headerRef.current?.clientHeight ?? undefined;
        setHeaderOptions([{ headerContainerHeight: headerRefHeight }]);

        // 收起弹窗的动效处理
        if (!isShowUserContent) {
            setTimeout(
                () => setHeaderOptions({ starkSx: zIndexAppBarInit }),
                ANIMATION_DURATION,
            );
        }
    }, [isShowUserContent, setHeaderOptions]);

    const stackAppendingClass = useMemo(() => {
        return {
            flexGrow: 1,
            ...starkSx,
        };
    }, [starkSx]);

    return (
        <FmStack
            spacing={2}
            className={classnames(stl["app-bar-stack-wrapper"], {
                ["z-index-under-drawer"]: !(isShowUserContent || visible),
            })}
            id={enforceZIndexUnderCurrentId}
            ref={headerRef}
            sx={stackAppendingClass}
        >
            {prop.children}
        </FmStack>
    );
};

export default HeaderContainerClient;
